import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles/UserDetails.modules.scss';
import useForm from 'hooks/useForm';
import { FieldTypeMap } from 'components/form-fields-v2';
import { BackToTop, ToggleButton } from 'components/quashed';
import { USER_DETAILS_FIELDS } from 'constants/user-details';
import { PAGE_VIEW_EVENTS, isDropdownField } from 'helpers';
import useTracking from 'hooks/useTracking';
import { IUser } from 'types';
import {
  getDefaultFormValues,
  validateProfileForm,
} from 'components/forms/utils';
import { UpdateUser } from 'actions';

const InputField = ({ form, field, disabled }: any) => {
  const type = field.type;
  const FieldComponent = FieldTypeMap[type];
  return (
    <FieldComponent
      basic={true}
      className={styles.fieldContainer}
      name={field.key}
      key={field.key}
      labelClassName={styles.label}
      inputClassName={classnames(
        !disabled && styles.editing,
        styles[`${field.type}Input`],
      )}
      onChange={
        isDropdownField(field.type)
          ? form.handleDropdownChange
          : form.handleInputChange
      }
      value={form.values[field.key]}
      disabled={disabled}
      icon={type === 'dropdown' && 'none'}
      disabledClassName={type === 'birthday' && styles.birthdayDisabled}
      search={field.key === 'occupation' && true}
      {...field}
    />
  );
};

const Fields = ({ isEdit, fields, form, title = '' }) => {
  return (
    <>
      {title && <div className={styles.subTitle}>{title}</div>}
      {fields.map((field) => {
        return (
          <InputField
            key={field.key}
            form={form}
            field={field}
            disabled={!isEdit}
          />
        );
      })}
    </>
  );
};

const UserDetails = () => {
  useTracking(PAGE_VIEW_EVENTS.User);

  const dispatch = useDispatch();
  const ref = useRef(null);
  const user = useSelector<any, IUser>(R.prop('user'));
  const [isEdit, setEdit] = useState(false);
  const [errors, setErrors] = useState(false);

  const updateUser = (u: IUser) => dispatch(UpdateUser(u));

  const defaultValues = useMemo(() => getDefaultFormValues(user), [user]);

  const onSubmit = useCallback(
    ({ values }) => {
      const formErrors = validateProfileForm(values);
      if (formErrors) {
        setErrors(formErrors);
        return;
      }

      const payload = {
        ...user,
        ...values,
        name: `${values.firstName} ${values.lastName}`,
      };

      updateUser(payload);
      setEdit(false);
      setErrors(null);
    },
    [user, updateUser],
  );

  const form = useForm({
    defaultValues,
    onDefaultValueUpdate: (prevValues) => R.mergeLeft(user, prevValues),
    onSubmit,
  });

  const toggleEdit = (e) => {
    e.preventDefault();
    setEdit(true);
  };

  const handleScrollToTop = () => {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <form className={styles.container} onSubmit={form.handleSubmit} ref={ref}>
      <div className={styles.header}>
        <div className={styles.subTitle}>Personal</div>
        <ToggleButton
          className={styles.editButtonContainer}
          onClick={toggleEdit}
          toggled={isEdit}
        />
      </div>
      <div className={styles.detailsContainer}>
        <Fields
          isEdit={isEdit}
          fields={USER_DETAILS_FIELDS['Personal']}
          form={form}
        />
        <Fields
          isEdit={isEdit}
          fields={USER_DETAILS_FIELDS['Finances']}
          form={form}
          title="Finances"
        />
        <Fields
          isEdit={isEdit}
          fields={USER_DETAILS_FIELDS['Assets']}
          form={form}
          title="Assets"
        />
      </div>
      <BackToTop onClick={handleScrollToTop} />
    </form>
  );
};

export default UserDetails;
